import React, { useEffect, useCallback, useState } from 'react';
import {
  DataGrid,
  GridColDef,
  GridSortModel,
  GridFilterModel,
  getGridStringOperators,
} from '@mui/x-data-grid';
import { Box, Button, Typography } from '@mui/material';
import CustomPagination from 'components/common/pagination/pagination';
import SiteSelector from 'components/common/SiteSelector/SiteSelector';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/pagination';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchUsers, setParams, resetParams } from 'store/usersSlice';
import { formatDate } from 'utils/date';
import { commonGridStyles } from 'styles/gridStyles';
import commonStyles from 'styles/common.module.scss';
import styles from './clients.module.scss';

const Clients: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, total, isLoading, params } = useAppSelector((state) => state.users);
  const navigate = useNavigate();
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  const customStringOperators = getGridStringOperators().filter(
    (operator) => operator.value === 'contains',
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);

      // Map current filter items to query parameters
      const newFilters: Record<string, any> = {};

      // Clear previous filters by setting them to undefined
      if (params) {
        Object.keys(params).forEach((key) => {
          if (key !== 'page' && key !== 'limit' && key !== 'sortField' && key !== 'sortValue') {
            newFilters[key] = undefined;
          }
        });
      }

      // Set new filter values
      model.items.forEach((item) => {
        if (item.field && item.value !== undefined && item.value !== '') {
          newFilters[item.field] = item.value;
        }
      });

      dispatch(setParams({ ...newFilters, page: 1 }));
    },
    [dispatch, params],
  );

  const renderVerificationCell = (params: any) => {
    const isVerified = params.value;
    return (
      <div className={styles.verificationCell}>
        <span className={styles.statusText}>{isVerified ? 'Yes' : 'No'}</span>
      </div>
    );
  };

  const loadData = useCallback(() => {
    dispatch(fetchUsers(params));
  }, [dispatch, params]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset params when the component unmounts
  useEffect(() => {
    return () => {
      dispatch(resetParams());
    };
  }, [dispatch]);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70, filterable: false },
    {
      field: 'username',
      headerName: 'Username',
      width: 130,
      sortable: true,
      filterOperators: customStringOperators,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 200,
      sortable: true,
      filterOperators: customStringOperators,
    },
    { field: 'phone', headerName: 'Phone', width: 150, filterOperators: customStringOperators },
    // { field: 'site', headerName: 'Site ID', width: 90 },

    {
      field: 'isEmailVerified',
      headerName: 'Email Ver.',
      width: 80,
      renderCell: renderVerificationCell,
      filterable: false,
      sortable: false,
    },
    {
      field: 'isPhoneVerified',
      headerName: 'Phone Ver.',
      width: 80,
      renderCell: renderVerificationCell,
      filterable: false,
      sortable: false,
    },
    {
      field: 'site',
      headerName: 'Site',
      width: 130,
      filterOperators: customStringOperators,
      renderCell: (params) => params.row.site?.title || '-',
    },

    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 180,
      sortable: true,

      filterable: false,
      renderCell: (params) => (params.row.createdAt ? formatDate(params.row.createdAt) : '-'),
    },

    {
      field: 'actions',
      headerName: '',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          className={styles.detailsBtn}
          onClick={() => navigate(`/clients/${params.id}`)}
        >
          Details
        </Button>
      ),
    },
  ];

  const handlePageChange = (page: number) => {
    dispatch(setParams({ page }));
  };

  const handlePageSizeChange = (limit: number) => {
    dispatch(setParams({ limit, page: 1 }));
  };

  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      dispatch(
        setParams({
          sortField: model[0].field,
          sortValue: model[0].sort?.toUpperCase() as 'ASC' | 'DESC',
        }),
      );
    }
  };

  const renderPagination = () => (
    <CustomPagination
      count={total || 0}
      page={params.page - 1}
      onPageChange={(_, newPage) => handlePageChange(newPage + 1)}
      rowsPerPage={params.limit}
      onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
    />
  );

  return (
    <Box className={styles.clientsContainer}>
      <Box className={styles.header}>
        <Typography variant="h4" className={styles.title}>
          Clients
        </Typography>
        <Box className={styles.controls}>
          <Box className={styles.filterItem}>
            <SiteSelector
              value={params.siteId || 'all'}
              onChange={(value) =>
                dispatch(setParams({ siteId: value === 'all' ? undefined : value, page: 1 }))
              }
              minWidth={130}
            />
          </Box>
        </Box>
      </Box>

      <Box className={commonStyles.gridWrapper}>
        {renderPagination()}
        <DataGrid
          rows={data || []}
          rowCount={total || 0}
          getRowId={(row: any) => row.id}
          columns={columns}
          onSortModelChange={handleSortModelChange}
          loading={isLoading}
          sx={commonGridStyles}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
        />
        {renderPagination()}
      </Box>
    </Box>
  );
};

export default Clients;
